<template>
  <div data-test="eventStatus" :data-event-actions-dialog="'ac-' + event.id">
    <div class="pa-2 my-3">
      <div v-if="event.notes && event.notes.length" class="event-notes">
        <div v-for="eventNote in event.notes" :key="eventNote.id" class="mb-2">
          <div class="event-notes--info">
            {{ eventNote.createdByPerson.name }}
            <span class="muted--text">
              |
              {{
                $t(
                  `events:EventPage.eventNoteRelatedAction.${eventNote.relatedAction}.name`,
                  eventNote.relatedAction
                )
              }}
              | {{ $dayjs(eventNote.createdAt).fromNow() }}</span
            >
          </div>
          <div class="event-notes--note">
            {{ eventNote.content }}
          </div>
        </div>
      </div>

      <v-textarea
        v-if="$allowedMutation(event.allowedMutations, 'eventAddNote')"
        v-model="note"
        data-test="leaveNote"
        rows="3"
        :label="$t('events:EventPage.eventNote.LeaveANote.label')"
        hide-details
        outlined
      />
    </div>
    <v-row dense class="flex-row-reverse justify-end">
      <div
        v-if="statusLoading"
        class="px-4 mt-2"
        data-test="eventStatusLoading"
      >
        <v-btn
          id="statusLoading"
          min-width="140px"
          height="40px!important"
          loading
          disabled
        />
      </div>
      <template v-else>
        <div
          v-for="item in mutatingActions"
          :key="item.mutationName"
          class="px-4 mt-2"
        >
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                :color="item.deletion ? 'red' : 'accent'"
                :data-test="item.mutationName"
                :outlined="item.decline"
                active-class
                min-width="140px"
                height="40px!important"
                v-bind="attrs"
                dark
                style="text-transform: capitalize !important"
                v-on="on"
                @click="handleAction(item)"
                >{{ $t(item.buttonNameKey, item.buttonName) }}</v-btn
              >
            </template>
            <span v-if="item.tooltip">{{
              $t(item.tooltipKey, item.tooltip)
            }}</span>
          </v-tooltip>
        </div>
        <div
          v-if="$allowedMutation(event.allowedMutations, 'eventAddNote')"
          class="px-4 mt-2"
          :class="$vuetify.breakpoint.mdAndDown ? 'ma-auto' : undefined"
        >
          <v-btn
            color="edit"
            data-test="eventAddNote"
            :disabled="statusLoading"
            :loading="addingNote"
            :width="$vuetify.breakpoint.mdAndDown ? '295px' : undefined"
            dark
            min-width="140px"
            height="40px!important"
            @click="addNote"
          >
            {{ $t("events:EventPage.eventNote.LeaveANote.button") }}
          </v-btn>
        </div>
      </template>
      <AppDialogContent
        v-if="$allowedMutation(event.allowedMutations, 'eventProposeSchedule')"
        v-model="confirmDialog"
        data-test="eventProposeScheduleConfirm"
        @submit-form="proposeScheduleConfirmed"
      >
        <template #header>
          <h4>
            {{
              $t(
                "events:EventPage.Actions.SchedulePropose.ConfirmationModal.heading"
              )
            }}
          </h4>
        </template>

        <v-row dense justify="center">
          <v-col class="text-center">
            <v-tooltip bottom>
              <template #activator="{ attrs, on }">
                <div v-bind="attrs" v-on="on">
                  <v-icon v-if="event.useOnlinePayments" x-large color="green">
                    $iconApproved
                  </v-icon>
                  <v-icon v-else x-large color="red"> $iconRejected </v-icon>
                </div>
              </template>
              <span>
                <template v-if="event.useOnlinePayments">
                  {{
                    $t(
                      "events:EventPage.Actions.SchedulePropose.ConfirmationModal.PaymentsActivated.tooltip"
                    )
                  }}
                </template>
                <template v-else>
                  {{
                    $t(
                      "events:EventPage.Actions.SchedulePropose.ConfirmationModal.PaymentsNotActivated.tooltip"
                    )
                  }}
                </template>
              </span>
            </v-tooltip>
            <div class="my-2">
              <b>
                {{
                  $t(
                    "events:EventPage.Actions.SchedulePropose.ConfirmationModal.Payments.label",
                    "Payments"
                  )
                }}
              </b>
            </div>
          </v-col>
          <v-col
            v-if="isFeatureFlagSet('useOnlinePaymentsForPrizeMoney')"
            class="text-center"
          >
            <v-tooltip bottom>
              <template #activator="{ attrs, on }">
                <div v-bind="attrs" v-on="on">
                  <v-icon
                    v-if="event.useOnlinePaymentsForPrizeMoney"
                    x-large
                    color="green"
                  >
                    $iconApproved
                  </v-icon>
                  <v-icon v-else x-large color="red"> $iconRejected </v-icon>
                </div>
              </template>
              <span>
                <template v-if="event.useOnlinePaymentsForPrizeMoney">
                  {{
                    $t(
                      "events:EventPage.Actions.SchedulePropose.ConfirmationModal.PrizesPayoutsActivated.tooltip"
                    )
                  }}
                </template>
                <template v-else>
                  {{
                    $t(
                      "events:EventPage.Actions.SchedulePropose.ConfirmationModal.PrizesPayoutsNotActivated.tooltip"
                    )
                  }}
                </template>
              </span>
            </v-tooltip>
            <div class="my-2">
              <b>
                {{
                  $t(
                    "events:EventPage.Actions.SchedulePropose.ConfirmationModal.PrizesPayouts.label",
                    "Prize payouts"
                  )
                }}
              </b>
            </div>
          </v-col>
          <v-col class="text-center">
            <v-tooltip bottom>
              <template #activator="{ attrs, on }">
                <div v-bind="attrs" v-on="on">
                  <v-icon v-if="event.useEquipe" x-large color="green">
                    $iconApproved
                  </v-icon>
                  <v-icon v-else x-large color="red"> $iconRejected </v-icon>
                </div>
              </template>
              <span>
                <template v-if="event.useEquipe">
                  {{
                    $t(
                      "events:EventPage.Actions.SchedulePropose.ConfirmationModal.EquipeActivated.tooltip"
                    )
                  }}
                </template>
                <template v-else>
                  {{
                    $t(
                      "events:EventPage.Actions.SchedulePropose.ConfirmationModal.EquipeNotActivated.tooltip"
                    )
                  }}
                </template>
              </span>
            </v-tooltip>
            <div class="my-2">
              <b>
                {{
                  $t(
                    "events:EventPage.Actions.SchedulePropose.ConfirmationModal.Equipe.label",
                    "Equipe APP"
                  )
                }}
              </b>
            </div>
          </v-col>
        </v-row>

        <AppAlertNotice>
          {{
            $t(
              "events:EventPage.Actions.SchedulePropose.ConfirmationModal.Alert.text"
            )
          }}
        </AppAlertNotice>
        <ul>
          <li
            v-if="
              i18next.exists(
                'events:EventPage.Actions.SchedulePropose.ConfirmationModal.Setting1.text'
              )
            "
          >
            {{
              $t(
                "events:EventPage.Actions.SchedulePropose.ConfirmationModal.Setting1.text"
              )
            }}
          </li>
          <li
            v-if="
              i18next.exists(
                'events:EventPage.Actions.SchedulePropose.ConfirmationModal.Setting2.text'
              )
            "
          >
            {{
              $t(
                "events:EventPage.Actions.SchedulePropose.ConfirmationModal.Setting2.text"
              )
            }}
          </li>
          <li
            v-if="
              i18next.exists(
                'events:EventPage.Actions.SchedulePropose.ConfirmationModal.Setting3.text'
              )
            "
          >
            {{
              $t(
                "events:EventPage.Actions.SchedulePropose.ConfirmationModal.Setting3.text"
              )
            }}
          </li>
          <li
            v-if="
              i18next.exists(
                'events:EventPage.Actions.SchedulePropose.ConfirmationModal.Setting4.text'
              )
            "
          >
            {{
              $t(
                "events:EventPage.Actions.SchedulePropose.ConfirmationModal.Setting4.text"
              )
            }}
          </li>
          <li
            v-if="
              i18next.exists(
                'events:EventPage.Actions.SchedulePropose.ConfirmationModal.Setting5.text'
              )
            "
          >
            {{
              $t(
                "events:EventPage.Actions.SchedulePropose.ConfirmationModal.Setting5.text"
              )
            }}
          </li>
          <li
            v-if="
              i18next.exists(
                'events:EventPage.Actions.SchedulePropose.ConfirmationModal.Setting6.text'
              )
            "
          >
            {{
              $t(
                "events:EventPage.Actions.SchedulePropose.ConfirmationModal.Setting6.text"
              )
            }}
          </li>
        </ul>
      </AppDialogContent>
    </v-row>
  </div>
</template>
<script>
import gql from "fraql";
import i18next from "i18next";
import { getFederationConfiguration } from "@composables/masterdata/config";
import AppDialogContent from "@elements/AppDialogContent.vue";
import AppAlertNotice from "@elements/AppAlertNotice/index.vue";
const MUTATING_ACTIONS = [
  {
    mutationName: "eventPropose",
    buttonNameKey: "events:EventPage.Actions.EventPropose.name",
    buttonName: "Propose",
    tooltipKey: "events:EventPage.Actions.EventPropose.tooltip",
    tooltip:
      "Propose event to calendar, organisers and officials will see your event and can approve it for publication in calendar"
  },
  {
    mutationName: "eventPublish",
    buttonNameKey: "events:EventPage.Actions.EventPublish.name",
    buttonName: "Publish",
    tooltipKey: "events:EventPage.Actions.EventPublish.tooltip",
    tooltip:
      "Publish event to calendar, event status will be changed to PUBLISHED"
  },
  {
    mutationName: "eventDecline",
    buttonNameKey: "events:EventPage.Actions.EventDecline.name",
    buttonName: "Decline",
    decline: true,
    tooltipKey: "events:EventPage.Actions.EventDecline.tooltip",
    tooltip: "Decline event - event status will be changed to DRAFT"
  },
  {
    mutationName: "eventProposeSchedule",
    buttonNameKey: "events:EventPage.Actions.SchedulePropose.name",
    buttonName: "Propose Schedule",
    tooltipKey: "events:EventPage.Actions.SchedulePropose.tooltip",
    tooltip:
      "Schedule will be proposed for approval and publication, only officials who have permissions to publish schedule (President of Ground Jury or regional or national SRL officials) can do it"
  },
  {
    mutationName: "eventPublishSchedule",
    buttonNameKey: "events:EventPage.Actions.SchedulePublish.name",
    buttonName: "Publish Schedule",
    tooltipKey: "events:EventPage.Actions.SchedulePublish.tooltip",
    tooltip:
      "Publish schedule will open registration. After publication athletes can start registering to classes."
  },
  {
    mutationName: "noncompetitionEventOpenRegistration",
    buttonNameKey:
      "events:EventPage.Actions.NoncompetitionEventOpenRegistration.Name",
    buttonName: "Open registration",
    tooltipKey:
      "events:EventPage.Actions.NoncompetitionEventOpenRegistration.tooltip",
    tooltip: "Publish the event to calendar and open for registration"
  },
  {
    mutationName: "eventDeclineSchedule",
    buttonNameKey: "events:EventPage.Actions.ScheduleDecline.name",
    buttonName: "Decline Schedule",
    tooltipKey: "events:EventPage.Actions.ScheduleDecline.tooltip",
    decline: true,
    tooltip:
      "Schedule will be declined and event status changed to PUBLISHED. After changing schedule, it can be proposed again"
  },
  {
    mutationName: "eventDelete",
    buttonNameKey: "events:EventPage.Actions.EventDelete.name",
    buttonName: "Delete",
    tooltipKey: "events:EventPage.Actions.EventDelete.tooltip",
    decline: true,
    deletion: true,
    tooltip: "Delete event"
  },
  {
    mutationName: "eventUnpublish",
    buttonNameKey: "events:EventPage.Actions.eventUnpublish.name",
    buttonName: "Unpublish",
    tooltipKey: "events:EventPage.Actions.eventUnpublish.tooltip",
    decline: true,
    tooltip: "Unpublish event"
  },
  {
    mutationName: "eventDeletePublished",
    buttonNameKey: "events:EventPage.Actions.EventDeletePublished.name",
    buttonName: "Delete",
    tooltipKey: "events:EventPage.Actions.EventDeletePublished.tooltip",
    decline: true,
    deletion: true,
    tooltip: "Delete published event"
  }
];
export default {
  name: "EventStatusActions",
  components: { AppAlertNotice, AppDialogContent },
  inject: {
    calendarRefetch: {
      default: () => null
    }
  },
  props: {
    event: {
      type: Object,
      required: true
    }
  },
  setup() {
    return {
      i18next,
      ...getFederationConfiguration()
    };
  },
  data() {
    return {
      statusLoading: false,
      tooltip: null,
      confirmed: false,
      confirmDialog: false,
      note: null,
      addingNote: false
    };
  },
  computed: {
    mutatingActions() {
      return MUTATING_ACTIONS.filter(mutation =>
        this.$allowedMutation(
          this.event.allowedMutations,
          mutation.mutationName
        )
      );
    }
  },
  mounted() {
    this.scrollNotes(); // using this.$el.querySelector somehow makes jest detect failure to teardown. In vitest it fails to finish
  },
  fragments: {
    event: gql`
      fragment _ on Event {
        id
        status
        startDate
        endDate
        registrationEndDate
        noncompetition
        allowedMutations
        notes {
          id
          relatedAction
          content
          createdAt
          createdByPerson {
            id
            name
          }
        }
      }
    `
  },
  methods: {
    scrollNotes() {
      this.$nextTick(() => {
        const lastNote = document.querySelector(
          `[data-event-actions-dialog='ac-${this.event.id}'] .event-notes > div:last-child`
        );
        if (lastNote) lastNote.scrollIntoView();
      });
    },
    proposeScheduleConfirmed() {
      this.confirmed = true;
      this.confirmDialog = false;
      this.handleAction({ mutationName: "eventProposeSchedule" });
    },
    async _handleDelete(item) {
      const { mutationName } = item;
      const response = await this.$root.$confirm(
        "Event deletion",
        "Are you sure you want to DELETE this show"
      );
      if (!response) return false;

      try {
        this.statusLoading = true;
        this.$emit("mutationLoading", true);

        await this.$apollo.mutate({
          mutation: gql`
            mutation ${mutationName} ($id: ID!) {
              ${mutationName} (input: { id: $id }) {
                event {
                  id
                }
              }
            }
          `,
          variables: {
            id: this.event.id
          }
        });
        this.$root.$snackbar("success", {
          text: `${this.event.name} deleted!`,
          redirection: true
        });

        if (["NonCompetitionCalendar", "Calendar"].includes(this.$route.name)) {
          this.calendarRefetch();
        } else {
          this.$router.push({
            name: this.event.noncompetition
              ? "NonCompetitionCalendar"
              : "Calendar"
          });
        }
      } catch (error) {
        this.$emit("mutationLoading", false);
        this.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        this.statusLoading = false;
      }
    },
    async handleAction(item) {
      if (item.deletion) {
        this._handleDelete(item);
        return;
      }
      const { mutationName } = item;
      if (mutationName == "eventProposeSchedule" && !this.confirmed) {
        // Get confirmation before proposing schedule
        this.confirmDialog = true;
        return;
      }
      this.statusLoading = true;
      this.$emit("mutationLoading", true);

      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation ${mutationName} (
              $id: ID!
              $note: String
            ) {
              ${mutationName} (input: {
                id: $id
                note: $note
              }) {
                event {
                  id
                  status
                  notes {
                    id
                    content
                    createdByPerson {
                      id
                      name
                    }
                    relatedAction
                    createdAt
                  }
                  allowedMutations
                }
              }
            }
          `,
          refetchQueries: ["eventSchedule"],
          awaitRefetchQueries: true,
          variables: {
            id: this.event.id,
            note: this.note
          }
        });

        this.note = null;
        this.$root.$snackbar("success");
        this.$emit("refetch");
        this.scrollNotes();
      } catch (error) {
        this.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        this.statusLoading = false;
        this.$emit("mutationLoading", false);
      }
    },
    async addNote() {
      this.addingNote = true;
      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation eventAddNote($id: ID!, $note: String) {
              eventAddNote(input: { id: $id, note: $note }) {
                event {
                  id
                  status
                  notes {
                    id
                    content
                    createdByPerson {
                      id
                      name
                    }
                    relatedAction
                    createdAt
                  }
                  allowedMutations
                }
              }
            }
          `,
          variables: {
            id: this.event.id,
            note: this.note
          }
        });

        this.note = null;
        this.$root.$snackbar("success", { text: "Note added" });
        this.scrollNotes();
      } catch (error) {
        this.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        this.addingNote = false;
        this.$emit("mutationLoading", false);
      }
    }
  }
};
</script>
<style scoped lang="scss">
.event-notes {
  height: 200px;
  overflow: auto;
  border-radius: 3px;
  border: 1px solid grey;
  padding: 10px;
  font-size: 12px;
  margin: 8px 0;

  &--note {
    border-radius: 3px;
    background-color: var(--v-primary-light-base);
    padding: 6px;
    margin: 2px 0;
  }
}

img {
  width: 38px;
}
</style>
